import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { apiPost } from '@/features/_shared/queries';
import { toast } from '@/lib/toast';

type PurgeResponse = {
  slug: string;
  counts: Record<string, number>;
  pdf_dir_removed: boolean;
};

export function TenantPurgeModal({
  slug,
  open,
  onClose,
  onPurged,
}: {
  slug: string;
  open: boolean;
  onClose: () => void;
  onPurged?: () => void;
}) {
  const [typed, setTyped] = useState('');

  const purge = useMutation({
    mutationFn: () => apiPost<PurgeResponse>(`/tenants/${slug}/purge`, { confirm: typed }),
    onSuccess: (result) => {
      const total = Object.values(result.counts).reduce((s, n) => s + n, 0);
      toast({
        tone: 'success',
        message: `Tenant '${result.slug}' onomkeerbaar verwijderd — ${total} rijen verwijderd · PDF-archief: ${
          result.pdf_dir_removed ? 'gewist' : 'niet gevonden'
        }`,
      });
      setTyped('');
      onPurged?.();
      onClose();
    },
    onError: (err: Error) => {
      toast({ tone: 'error', message: `Purge mislukt: ${err.message}` });
    },
  });

  if (!open) return null;

  const matches = typed === slug;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
        <div className="mb-2 flex items-center gap-2 text-red-700">
          <AlertTriangle size={16} />
          <h2 className="text-[15px] font-semibold">Tenant onomkeerbaar verwijderen</h2>
        </div>
        <p className="text-[12px] text-slate-600">
          Dit verwijdert <strong>{slug}</strong> en alle bijbehorende domeinen, rapporten, alerts,
          recipients en PDF-archieven van disk. <strong>Niet ongedaan te maken.</strong> De
          audit-log blijft intact als bewijs van de actie.
        </p>

        <label className="mt-4 block text-[12px] font-medium text-slate-700">
          Typ exact <code className="font-mono">{slug}</code> om te bevestigen:
        </label>
        <input
          type="text"
          autoFocus
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={slug}
          className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 font-mono text-[13px]"
          autoComplete="off"
          spellCheck={false}
        />

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              setTyped('');
              onClose();
            }}
            className="rounded border border-slate-300 px-3 py-1.5 text-[12px] text-slate-700 hover:bg-slate-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            disabled={!matches || purge.isPending}
            onClick={() => purge.mutate()}
            className="rounded bg-red-600 px-3 py-1.5 text-[12px] font-medium text-white hover:bg-red-700 disabled:opacity-40"
          >
            {purge.isPending ? 'Bezig met purgen…' : 'Permanent verwijderen'}
          </button>
        </div>
      </div>
    </div>
  );
}
